/**
 * Extraction Templates for Document Intelligence
 *
 * LLM extraction templates that define how structured data is extracted from
 * each document type. Used by the document pipeline's extraction stage and
 * referenced by the API gateway for reconciliation.
 *
 * Each template provides:
 * - A system prompt tailored to the document type
 * - A JSON schema for structured output generation
 * - The list of canonical data point keys it extracts
 */

import type { DocumentType } from '../constants/document-types';
import { DOCUMENT_TYPE_DISPLAY_NAMES, DOCUMENT_TYPES } from '../constants/document-types';

// ============================================================================
// EXTRACTION TEMPLATE INTERFACE
// ============================================================================

/**
 * Template for extracting structured data from a specific document type.
 */
export interface ExtractionTemplate {
  /** The document type this template handles */
  documentType: DocumentType;
  /** Human-readable name for the template */
  displayName: string;
  /** Description of what this template extracts */
  description: string;
  /** JSON schema for LLM structured output generation */
  jsonSchema: object;
  /** System prompt instructing the LLM on extraction behavior */
  systemPrompt: string;
  /** Canonical data point keys this template can extract */
  extractsFields: string[];
}

// ============================================================================
// JSON SCHEMA BUILDER
// ============================================================================

/**
 * Build the JSON schema for structured extraction output.
 * Each template reuses this base schema, parameterized by expected fields.
 *
 * @param expectedFields - Canonical keys the LLM should attempt to extract
 * @returns JSON schema object for GenerateStructuredOutput
 */
function buildExtractionSchema(expectedFields: string[]): object {
  return {
    type: 'object',
    properties: {
      fields: {
        type: 'array',
        description: `Extract values for these canonical keys where found: ${expectedFields.join(', ')}`,
        items: {
          type: 'object',
          properties: {
            canonicalKey: {
              type: 'string',
              enum: expectedFields,
              description: 'The canonical data point key',
            },
            rawText: {
              type: 'string',
              description: 'The exact text as found in the document',
            },
            normalizedValue: {
              type: 'string',
              description: 'The cleaned/normalized value (e.g., dates as ISO 8601, currency as plain numbers)',
            },
            confidence: {
              type: 'number',
              minimum: 0,
              maximum: 1,
              description: 'Confidence score from 0.0 (uncertain) to 1.0 (certain)',
            },
            pageNumber: {
              type: 'integer',
              description: 'The page number where the value was found (1-indexed)',
            },
            snippet: {
              type: 'string',
              description: 'Surrounding text context for verification',
            },
          },
          required: ['canonicalKey', 'rawText', 'confidence'],
        },
      },
      documentDate: {
        type: 'string',
        description: 'The date of the document (statement date, filing date, etc.) in ISO 8601 format',
      },
      documentPeriod: {
        type: 'string',
        description: 'The period covered by the document (e.g., "2025-01 to 2025-03", "Tax Year 2024")',
      },
    },
    required: ['fields'],
  };
}

/**
 * Build a system prompt for a specific document type and its target fields.
 *
 * @param documentTypeName - Human-readable document type name
 * @param fieldDescriptions - Descriptions of fields to extract
 * @returns Complete system prompt string
 */
function buildSystemPrompt(documentTypeName: string, fieldDescriptions: string): string {
  return (
    `You are a document extraction specialist for private lending loan origination. ` +
    `Extract the following fields from this ${documentTypeName}. ` +
    `For each field, provide the raw text exactly as found in the document, ` +
    `a normalized value (dates as ISO 8601, currency as plain numbers without symbols, ` +
    `SSN/EIN with dashes), your confidence score (0.0-1.0), and the page number where found. ` +
    `Only extract fields you can find with reasonable confidence. ` +
    `Do not guess or fabricate values.\n\n` +
    `Fields to extract:\n${fieldDescriptions}`
  );
}

// ============================================================================
// EXTRACTION TEMPLATES
// ============================================================================

const BANK_STATEMENT_PERSONAL_FIELDS = [
  'account_balance',
  'total_deposits',
  'monthly_income',
  'borrower_name',
  'borrower_address',
];

const BANK_STATEMENT_BUSINESS_FIELDS = ['account_balance', 'total_deposits', 'entity_name'];

const TAX_RETURN_PERSONAL_FIELDS = ['annual_income', 'borrower_name', 'ssn'];

const TAX_RETURN_BUSINESS_FIELDS = ['annual_income', 'entity_name', 'ein'];

const INCOME_VERIFICATION_FIELDS = [
  'monthly_income',
  'annual_income',
  'employer_name',
  'employment_position',
  'years_employed',
];

const DRIVERS_LICENSE_FIELDS = ['borrower_name', 'borrower_address', 'dob'];

const PROPERTY_APPRAISAL_FIELDS = ['property_address', 'current_value', 'after_repair_value', 'property_type'];

const PURCHASE_AGREEMENT_FIELDS = ['property_address', 'purchase_price'];

const RENT_ROLL_FIELDS = ['rental_income', 'occupancy_rate', 'noi'];

const OPERATING_AGREEMENT_FIELDS = ['entity_name', 'entity_type', 'state_of_formation'];

// ============================================================================
// CAPITAL SOURCE TEMPLATES (Issue #1631 M3)
// ============================================================================

/**
 * Custom nested JSON schema for warehouse-line credit-box extraction.
 * Output maps directly to WarehouseLineLoanTypeProfile + CreditBoxRule +
 * WarehouseLineRequiredDoc rows. Does NOT use the flat canonical-fields shape.
 */
const WAREHOUSE_LINE_CREDIT_BOX_SCHEMA = {
  type: 'object',
  properties: {
    capitalProviderName: {
      type: 'string',
      description: 'The institution name (Goldman Sachs, Loon Creek, etc.)',
    },
    warehouseLineName: {
      type: 'string',
      description: 'The line product name (e.g. "GS-Multi", "Loon Bridge Facility")',
    },
    facilityCap: {
      type: ['number', 'null'],
      description: 'Total facility cap in USD if specified',
    },
    advanceRatePercent: {
      type: ['number', 'null'],
      description: 'Default advance rate the fund extends to the lender (0.0-1.0). NULL if per-loan-type only.',
    },
    loanTypeProfiles: {
      type: 'array',
      description: 'One entry per loan type the line covers (Bridge / DSCR / Ground-Up / Fix-and-Flip / etc.)',
      items: {
        type: 'object',
        properties: {
          loanCategory: {
            type: 'string',
            enum: ['BRIDGE', 'DSCR', 'GROUND_UP', 'FIX_AND_FLIP', 'OTHER'],
            description: 'Canonical loan category. Map source-doc terms (e.g. "Construction" -> GROUND_UP)',
          },
          leverageCaps: {
            type: 'object',
            properties: {
              maxLtvPercent: { type: ['number', 'null'] },
              maxLtcPercent: { type: ['number', 'null'] },
              maxLtarvPercent: { type: ['number', 'null'] },
              minFico: { type: ['integer', 'null'] },
              minLoanAmount: { type: ['number', 'null'] },
              maxLoanAmount: { type: ['number', 'null'] },
            },
          },
          eligibilityRules: {
            type: 'array',
            description: 'Hard eligibility constraints. One rule per constraint.',
            items: {
              type: 'object',
              properties: {
                field: {
                  type: 'string',
                  description: 'Canonical field key (e.g. "fico", "ltv", "property_type", "borrower.foreign_national")',
                },
                op: {
                  type: 'string',
                  enum: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'in', 'notIn', 'matches'],
                },
                value: {
                  description:
                    'Comparison value. Number for arithmetic ops, array for between/in/notIn, string for matches.',
                },
                severity: {
                  type: 'string',
                  enum: ['BLOCK', 'WARN', 'INFO'],
                  description: 'BLOCK fires the exception desk; WARN annotates the result; INFO is informational only',
                },
                message: {
                  type: ['string', 'null'],
                  description: 'Human-readable explanation of why this rule exists',
                },
                citationPage: {
                  type: 'integer',
                  description: 'Page number where this rule was found (1-indexed)',
                },
                citationQuote: {
                  type: 'string',
                  description: 'Verbatim quote from the document supporting this rule',
                },
              },
              required: ['field', 'op', 'value', 'severity', 'citationPage', 'citationQuote'],
            },
          },
          requiredFields: {
            type: 'array',
            items: { type: 'string' },
            description:
              'Canonical field keys the lender must capture for this loan type (e.g. ["purchase_price", "rehab_budget", "exit_strategy"])',
          },
          requiredDocs: {
            type: 'array',
            description: 'Documents the case must have before this profile can fund. One entry per doc type.',
            items: {
              type: 'object',
              properties: {
                documentTypeKey: {
                  type: 'string',
                  description: 'Canonical document type key (e.g. "property_appraisal", "tax_return_personal")',
                },
                required: { type: 'boolean', default: true },
                trigger: {
                  type: ['object', 'null'],
                  description:
                    'Optional gating condition (only required when X). Same shape as eligibilityRules item without severity/citation.',
                },
                citationPage: { type: 'integer' },
                citationQuote: { type: 'string' },
              },
              required: ['documentTypeKey', 'citationPage', 'citationQuote'],
            },
          },
          advanceRatePercent: {
            type: ['number', 'null'],
            description: 'Per-loan-type advance rate override (overrides the line-level default if set)',
          },
          personalGuaranteeRequired: { type: 'boolean', default: true },
          maxHoldPeriodDays: { type: ['integer', 'null'] },
          takeoutWindowDays: { type: ['integer', 'null'] },
          defaultTermMonths: { type: ['integer', 'null'] },
          // Pricing adjustments (LLPAs) per D9
          pricingAdjustments: {
            type: 'array',
            description: 'Rate or points adjustments triggered by deal characteristics (LLPAs)',
            items: {
              type: 'object',
              properties: {
                description: { type: 'string' },
                condition: {
                  type: 'object',
                  properties: {
                    field: { type: 'string' },
                    op: {
                      type: 'string',
                      enum: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'in', 'notIn'],
                    },
                    value: {},
                  },
                  required: ['field', 'op', 'value'],
                },
                adjustment: {
                  type: 'object',
                  properties: {
                    type: { type: 'string', enum: ['rate_bps', 'points'] },
                    amount: { type: 'number' },
                  },
                  required: ['type', 'amount'],
                },
                citationPage: { type: 'integer' },
                citationQuote: { type: 'string' },
              },
              required: ['condition', 'adjustment', 'citationPage', 'citationQuote'],
            },
          },
        },
        required: ['loanCategory', 'leverageCaps', 'eligibilityRules', 'requiredFields', 'requiredDocs'],
      },
    },
    // D7 — global rules at the line level (apply to every profile)
    warehouseLineGlobalRules: {
      type: 'array',
      description: 'Cross-loan-type rules that apply across every profile under this line',
      items: {
        type: 'object',
        properties: {
          field: { type: 'string' },
          op: {
            type: 'string',
            enum: ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between', 'in', 'notIn', 'matches'],
          },
          value: {},
          severity: { type: 'string', enum: ['BLOCK', 'WARN', 'INFO'] },
          message: { type: ['string', 'null'] },
          citationPage: { type: 'integer' },
          citationQuote: { type: 'string' },
        },
        required: ['field', 'op', 'value', 'severity', 'citationPage', 'citationQuote'],
      },
    },
  },
  required: ['capitalProviderName', 'warehouseLineName', 'loanTypeProfiles'],
} as const;

const WAREHOUSE_LINE_CREDIT_BOX_SYSTEM_PROMPT =
  `You are a fund-provider credit-box extraction specialist for private lending. ` +
  `Extract the credit box from this fund-provider document. The fund provider ` +
  `(bank, hedge fund, or family office) has documented the lending criteria ` +
  `for one or more loan types they fund through a warehouse line. ` +
  `\n\nIMPORTANT: every eligibility rule MUST be decomposed into the structured ` +
  `shape {field, op, value, severity, citationPage, citationQuote}. Do NOT emit ` +
  `narrative rules as a single "rule" string — the downstream system rejects that. ` +
  `One human-readable rule becomes one structured object; complex rules become ` +
  `multiple structured objects (one per atomic constraint).` +
  `\n\nKey extraction rules:\n` +
  `1. Group by loan type — most providers split rules by Bridge / DSCR / ` +
  `   Ground-Up / Fix-and-Flip. Produce one entry per loan type detected, ` +
  `   even if some fields overlap. Use the field name "loanCategory" (NOT ` +
  `   "loanType") and pick from the enum BRIDGE | DSCR | GROUND_UP | ` +
  `   FIX_AND_FLIP | OTHER.\n` +
  `2. EVERY rule and required-doc MUST cite a page number and a verbatim quote. ` +
  `   If you can't find a rule explicitly stated, DO NOT invent it. Citation ` +
  `   validation rejects the entire extraction if any quote can't be matched ` +
  `   in the document text. citationPage is a positive integer; citationQuote ` +
  `   is the exact verbatim string from the document.\n` +
  `3. Map source-doc terminology to canonical loan categories: "Construction" ` +
  `   -> GROUND_UP, "Rental" -> DSCR, "Hard Money" -> BRIDGE (when context fits).\n` +
  `4. Eligibility rules use BLOCK severity (deal can't fund without exception). ` +
  `   Soft constraints use WARN. Use INFO for purely informational annotations.\n` +
  `5. Cross-loan-type rules ("no foreign nationals on any program") go in ` +
  `   warehouseLineGlobalRules, NOT duplicated into each loanTypeProfile.\n` +
  `6. Pricing adjustments (LLPAs — "+50bps for FN", "+1pt for cash-out") go ` +
  `   in pricingAdjustments. Specify rate_bps or points + amount.\n` +
  `7. If a numeric value isn't in the document, return null. Don't guess.\n` +
  `8. Leverage caps (LTV, FICO, loan size) live INSIDE the leverageCaps ` +
  `   object on the profile, NOT at the top of the profile. minLoanAmount + ` +
  `   maxLoanAmount are USD numbers (e.g. 250000), not strings.\n\n` +
  `Few-shot examples of correct rule decomposition:\n` +
  `\n` +
  `Document text: "Minimum FICO 660 for Bridge loans."\n` +
  `→ {field: "fico", op: "gte", value: 660, severity: "BLOCK", citationPage: 1, citationQuote: "Minimum FICO 660 for Bridge loans."}\n` +
  `\n` +
  `Document text: "Loan amount range $250K to $5M."\n` +
  `→ Two structured rules:\n` +
  `   {field: "loan_amount", op: "gte", value: 250000, severity: "BLOCK", citationPage: 1, citationQuote: "Loan amount range $250K to $5M."}\n` +
  `   {field: "loan_amount", op: "lte", value: 5000000, severity: "BLOCK", citationPage: 1, citationQuote: "Loan amount range $250K to $5M."}\n` +
  `\n` +
  `Document text: "No properties in flood zones."\n` +
  `→ {field: "property.flood_zone", op: "eq", value: false, severity: "BLOCK", citationPage: 2, citationQuote: "No properties in flood zones."}\n` +
  `\n` +
  `Document text: "Stabilized single-family properties only, exit to expected sale or refinance within 12 months."\n` +
  `→ Two structured rules:\n` +
  `   {field: "property_type", op: "in", value: ["single_family"], severity: "BLOCK", citationPage: 1, citationQuote: "Stabilized single-family properties only, exit to expected sale or refinance within 12 months."}\n` +
  `   {field: "max_hold_period_days", op: "lte", value: 365, severity: "BLOCK", citationPage: 1, citationQuote: "Stabilized single-family properties only, exit to expected sale or refinance within 12 months."}\n` +
  `\n` +
  `Document text: "75% max LTV for Bridge."\n` +
  `→ NOT a rule — this goes in leverageCaps.maxLtvPercent: 75 on the BRIDGE profile.\n` +
  `\n` +
  `\nCRITICAL: The TOP-LEVEL output is a single OBJECT with these keys:\n` +
  `  - capitalProviderName (string)\n` +
  `  - warehouseLineName (string)\n` +
  `  - loanTypeProfiles (array of profile objects)\n` +
  `  - warehouseLineGlobalRules (array of structured rules — same shape as eligibilityRules)\n` +
  `Do NOT return a bare array of profiles at the top level. Wrap them in loanTypeProfiles.\n\n` +
  `Example full output shape (top level wraps the profile array):\n` +
  `{\n` +
  `  "capitalProviderName": "1Sharpe",\n` +
  `  "warehouseLineName": "Loan Acquisition Line",\n` +
  `  "facilityCap": null,\n` +
  `  "advanceRatePercent": null,\n` +
  `  "loanTypeProfiles": [\n` +
  `    {\n` +
  `      "loanCategory": "BRIDGE",\n` +
  `      "leverageCaps": {"maxLtvPercent": 75, "minFico": 660, "minLoanAmount": 250000, "maxLoanAmount": 5000000, "maxLtcPercent": null, "maxLtarvPercent": null},\n` +
  `      "eligibilityRules": [{"field": "property_type", "op": "in", "value": ["single_family"], "severity": "BLOCK", "citationPage": 1, "citationQuote": "..."}],\n` +
  `      "requiredFields": ["purchase_price", "exit_strategy"],\n` +
  `      "requiredDocs": [{"documentTypeKey": "property_appraisal", "required": true, "citationPage": 3, "citationQuote": "..."}],\n` +
  `      "personalGuaranteeRequired": true,\n` +
  `      "pricingAdjustments": []\n` +
  `    }\n` +
  `  ],\n` +
  `  "warehouseLineGlobalRules": [\n` +
  `    {"field": "borrower.foreign_national", "op": "eq", "value": false, "severity": "BLOCK", "citationPage": 2, "citationQuote": "No foreign nationals."}\n` +
  `  ]\n` +
  `}`;

/**
 * Custom nested JSON schema for warehouse-line rate-sheet extraction.
 * Output maps directly to RateTier rows. Does NOT use the flat
 * canonical-fields shape.
 */
const WAREHOUSE_LINE_RATE_SHEET_SCHEMA = {
  type: 'object',
  properties: {
    capitalProviderName: { type: 'string' },
    warehouseLineName: { type: 'string' },
    effectiveDate: {
      type: ['string', 'null'],
      description: 'Effective date of these rates in ISO 8601',
    },
    expirationDate: {
      type: ['string', 'null'],
      description: 'When this rate sheet expires in ISO 8601',
    },
    rateTierVersions: {
      type: 'array',
      description: 'One entry per loan category × tier band on the rate sheet',
      items: {
        type: 'object',
        properties: {
          loanCategory: {
            type: 'string',
            enum: ['BRIDGE', 'DSCR', 'GROUND_UP', 'FIX_AND_FLIP', 'OTHER'],
          },
          rateBands: {
            type: 'array',
            description: 'Per-tier rate bands. Most rate sheets have multiple bands keyed by FICO + LTV + loan amount.',
            items: {
              type: 'object',
              properties: {
                minLoanAmount: { type: ['number', 'null'] },
                maxLoanAmount: { type: ['number', 'null'] },
                minFico: { type: ['integer', 'null'] },
                maxFico: { type: ['integer', 'null'] },
                minLtv: { type: ['number', 'null'] },
                maxLtv: { type: ['number', 'null'] },
                baseRatePercent: {
                  type: 'number',
                  description: 'The base interest rate for this band (e.g. 0.0925 = 9.25%)',
                },
                points: {
                  type: ['number', 'null'],
                  description: 'Origination points (e.g. 0.02 = 2 points)',
                },
                citationPage: { type: 'integer' },
                citationQuote: { type: 'string' },
              },
              required: ['baseRatePercent', 'citationPage', 'citationQuote'],
            },
          },
        },
        required: ['loanCategory', 'rateBands'],
      },
    },
  },
  required: ['capitalProviderName', 'warehouseLineName', 'rateTierVersions'],
} as const;

const WAREHOUSE_LINE_RATE_SHEET_SYSTEM_PROMPT =
  `You are a fund-provider rate-sheet extraction specialist for private lending. ` +
  `Extract the rate matrix from this rate sheet. ` +
  `\n\nKey extraction rules:\n` +
  `1. Group by loan category. Each category may have multiple rate bands ` +
  `   keyed by FICO + LTV + loan amount.\n` +
  `2. Rates as decimals (9.25% = 0.0925, 2 points = 0.02).\n` +
  `3. EVERY band MUST have a page citation + verbatim quote.\n` +
  `4. If a band's bounds aren't in the document (e.g. no max loan amount ` +
  `   stated), return null — don't infer from neighboring bands.\n` +
  `5. Effective and expiration dates: extract if stated; null otherwise.`;

/**
 * Extraction templates keyed by document type string.
 * Each template defines the LLM instructions and schema for extracting
 * structured data from a specific document type.
 */
export const EXTRACTION_TEMPLATES: Record<string, ExtractionTemplate> = {
  [DOCUMENT_TYPES.BANK_STATEMENT_PERSONAL]: {
    documentType: DOCUMENT_TYPES.BANK_STATEMENT_PERSONAL,
    displayName: 'Personal Bank Statement Extraction',
    description:
      'Extracts account balances, deposit totals, income patterns, and account holder information from personal bank statements.',
    jsonSchema: buildExtractionSchema(BANK_STATEMENT_PERSONAL_FIELDS),
    systemPrompt: buildSystemPrompt(
      'personal bank statement',
      [
        '- account_balance: The ending/closing balance for the statement period',
        '- total_deposits: The sum of all deposits during the statement period',
        '- monthly_income: Regular income deposits (payroll, transfers) identified during the period',
        '- borrower_name: The account holder name as shown on the statement',
        '- borrower_address: The mailing address as shown on the statement',
      ].join('\n'),
    ),
    extractsFields: BANK_STATEMENT_PERSONAL_FIELDS,
  },

  [DOCUMENT_TYPES.BANK_STATEMENT_BUSINESS]: {
    documentType: DOCUMENT_TYPES.BANK_STATEMENT_BUSINESS,
    displayName: 'Business Bank Statement Extraction',
    description: 'Extracts account balances, deposit totals, and entity information from business bank statements.',
    jsonSchema: buildExtractionSchema(BANK_STATEMENT_BUSINESS_FIELDS),
    systemPrompt: buildSystemPrompt(
      'business bank statement',
      [
        '- account_balance: The ending/closing balance for the statement period',
        '- total_deposits: The sum of all deposits during the statement period',
        '- entity_name: The business/entity name on the account',
      ].join('\n'),
    ),
    extractsFields: BANK_STATEMENT_BUSINESS_FIELDS,
  },

  [DOCUMENT_TYPES.TAX_RETURN_PERSONAL]: {
    documentType: DOCUMENT_TYPES.TAX_RETURN_PERSONAL,
    displayName: 'Personal Tax Return Extraction',
    description: 'Extracts income figures, taxpayer identity, and SSN from personal tax returns (Form 1040).',
    jsonSchema: buildExtractionSchema(TAX_RETURN_PERSONAL_FIELDS),
    systemPrompt: buildSystemPrompt(
      'personal tax return (Form 1040)',
      [
        '- annual_income: Adjusted Gross Income (AGI) from Line 11, or Total Income from Line 9',
        '- borrower_name: The taxpayer name as shown on the return',
        '- ssn: The Social Security Number (format: XXX-XX-XXXX)',
      ].join('\n'),
    ),
    extractsFields: TAX_RETURN_PERSONAL_FIELDS,
  },

  [DOCUMENT_TYPES.TAX_RETURN_BUSINESS]: {
    documentType: DOCUMENT_TYPES.TAX_RETURN_BUSINESS,
    displayName: 'Business Tax Return Extraction',
    description: 'Extracts business income, entity name, and EIN from business tax returns (Form 1120/1065/1120-S).',
    jsonSchema: buildExtractionSchema(TAX_RETURN_BUSINESS_FIELDS),
    systemPrompt: buildSystemPrompt(
      'business tax return (Form 1120, 1065, or 1120-S)',
      [
        '- annual_income: Gross receipts or net income from the return',
        '- entity_name: The business name as shown on the return',
        '- ein: The Employer Identification Number (format: XX-XXXXXXX)',
      ].join('\n'),
    ),
    extractsFields: TAX_RETURN_BUSINESS_FIELDS,
  },

  [DOCUMENT_TYPES.INCOME_VERIFICATION]: {
    documentType: DOCUMENT_TYPES.INCOME_VERIFICATION,
    displayName: 'Income Verification Extraction',
    description:
      'Extracts employment and income details from pay stubs, VOE letters, or employment verification documents.',
    jsonSchema: buildExtractionSchema(INCOME_VERIFICATION_FIELDS),
    systemPrompt: buildSystemPrompt(
      'income verification document (pay stub, VOE, or employment letter)',
      [
        '- monthly_income: Gross monthly pay or salary',
        '- annual_income: Gross annual salary or annualized income',
        '- employer_name: The name of the employer',
        '- employment_position: The job title or position',
        '- years_employed: Duration of employment in years (calculate from start date if needed)',
      ].join('\n'),
    ),
    extractsFields: INCOME_VERIFICATION_FIELDS,
  },

  [DOCUMENT_TYPES.DRIVERS_LICENSE]: {
    documentType: DOCUMENT_TYPES.DRIVERS_LICENSE,
    displayName: "Driver's License Extraction",
    description: "Extracts borrower identity information from a driver's license or state-issued ID.",
    jsonSchema: buildExtractionSchema(DRIVERS_LICENSE_FIELDS),
    systemPrompt: buildSystemPrompt(
      "driver's license or state-issued ID",
      [
        '- borrower_name: Full legal name as shown on the ID',
        '- borrower_address: Residential address as shown on the ID',
        '- dob: Date of birth (normalize to ISO 8601 YYYY-MM-DD format)',
      ].join('\n'),
    ),
    extractsFields: DRIVERS_LICENSE_FIELDS,
  },

  [DOCUMENT_TYPES.PROPERTY_APPRAISAL]: {
    documentType: DOCUMENT_TYPES.PROPERTY_APPRAISAL,
    displayName: 'Property Appraisal Extraction',
    description: 'Extracts property valuation, address, and type from appraisal reports.',
    jsonSchema: buildExtractionSchema(PROPERTY_APPRAISAL_FIELDS),
    systemPrompt: buildSystemPrompt(
      'property appraisal report',
      [
        '- property_address: The full street address of the appraised property',
        '- current_value: The appraised "as-is" market value',
        '- after_repair_value: The "as-completed" or "after repair" value, if provided',
        '- property_type: The property type classification (e.g., Single Family, Multi-Family, Commercial, Mixed Use)',
      ].join('\n'),
    ),
    extractsFields: PROPERTY_APPRAISAL_FIELDS,
  },

  [DOCUMENT_TYPES.PURCHASE_AGREEMENT]: {
    documentType: DOCUMENT_TYPES.PURCHASE_AGREEMENT,
    displayName: 'Purchase Agreement Extraction',
    description: 'Extracts property address and purchase price from purchase and sale agreements.',
    jsonSchema: buildExtractionSchema(PURCHASE_AGREEMENT_FIELDS),
    systemPrompt: buildSystemPrompt(
      'purchase and sale agreement',
      [
        '- property_address: The full street address of the property being purchased',
        '- purchase_price: The agreed-upon purchase price',
      ].join('\n'),
    ),
    extractsFields: PURCHASE_AGREEMENT_FIELDS,
  },

  [DOCUMENT_TYPES.RENT_ROLL]: {
    documentType: DOCUMENT_TYPES.RENT_ROLL,
    displayName: 'Rent Roll Extraction',
    description: 'Extracts rental income, occupancy rates, and NOI from rent roll documents.',
    jsonSchema: buildExtractionSchema(RENT_ROLL_FIELDS),
    systemPrompt: buildSystemPrompt(
      'rent roll',
      [
        '- rental_income: Total monthly or annual rental income across all units',
        '- occupancy_rate: Current occupancy rate as a percentage (0-100)',
        '- noi: Net Operating Income if stated, or calculate from gross rental income minus operating expenses if both are available',
      ].join('\n'),
    ),
    extractsFields: RENT_ROLL_FIELDS,
  },

  [DOCUMENT_TYPES.OPERATING_AGREEMENT]: {
    documentType: DOCUMENT_TYPES.OPERATING_AGREEMENT,
    displayName: 'Operating Agreement Extraction',
    description: 'Extracts entity identity and formation details from LLC operating agreements or corporate documents.',
    jsonSchema: buildExtractionSchema(OPERATING_AGREEMENT_FIELDS),
    systemPrompt: buildSystemPrompt(
      'operating agreement or entity formation document',
      [
        '- entity_name: The full legal name of the LLC or corporation',
        '- entity_type: The entity classification (e.g., LLC, Corporation, Limited Partnership, S-Corp)',
        '- state_of_formation: The state where the entity was formed/organized',
      ].join('\n'),
    ),
    extractsFields: OPERATING_AGREEMENT_FIELDS,
  },

  [DOCUMENT_TYPES.WAREHOUSE_LINE_CREDIT_BOX]: {
    documentType: DOCUMENT_TYPES.WAREHOUSE_LINE_CREDIT_BOX,
    displayName: 'Warehouse Line Credit Box Extraction',
    description:
      'Extracts the per-loan-type credit box (leverage caps, eligibility rules, required docs, pricing adjustments) from a fund-provider credit-box document. Output is a nested loanTypeProfiles[] structure that maps directly to WarehouseLineLoanTypeProfile + CreditBoxRule + WarehouseLineRequiredDoc rows.',
    jsonSchema: WAREHOUSE_LINE_CREDIT_BOX_SCHEMA,
    systemPrompt: WAREHOUSE_LINE_CREDIT_BOX_SYSTEM_PROMPT,
    extractsFields: [], // empty — this template uses a custom nested schema, not flat canonical fields
  },

  [DOCUMENT_TYPES.WAREHOUSE_LINE_RATE_SHEET]: {
    documentType: DOCUMENT_TYPES.WAREHOUSE_LINE_RATE_SHEET,
    displayName: 'Warehouse Line Rate Sheet Extraction',
    description:
      'Extracts the rate matrix (per loan category, per FICO×LTV×loan-amount band) from a fund-provider rate sheet. Output maps to RateTier rows.',
    jsonSchema: WAREHOUSE_LINE_RATE_SHEET_SCHEMA,
    systemPrompt: WAREHOUSE_LINE_RATE_SHEET_SYSTEM_PROMPT,
    extractsFields: [], // empty — this template uses a custom nested schema, not flat canonical fields
  },
};

// ============================================================================
// CLASSIFICATION TEMPLATE
// ============================================================================

/**
 * All document type values for the classification prompt.
 */
const ALL_DOCUMENT_TYPE_VALUES = Object.entries(DOCUMENT_TYPE_DISPLAY_NAMES)
  .map(([key, name]) => `  - "${key}" (${name})`)
  .join('\n');

/**
 * Lightweight template for classifying an unknown document into a known type.
 * Used as a preprocessing step before applying an extraction template.
 */
export const CLASSIFICATION_TEMPLATE = {
  systemPrompt:
    `You are a document classifier for private lending loan origination. ` +
    `Analyze the document content and determine its type from the following list. ` +
    `Return the most specific matching document type and your confidence level. ` +
    `If the document does not match any type well, use "other".\n\n` +
    `Possible document types:\n${ALL_DOCUMENT_TYPE_VALUES}`,
  jsonSchema: {
    type: 'object',
    properties: {
      documentType: {
        type: 'string',
        description: 'The classified document type key',
      },
      confidence: {
        type: 'number',
        minimum: 0,
        maximum: 1,
        description: 'Classification confidence from 0.0 to 1.0',
      },
      reasoning: {
        type: 'string',
        description: 'Brief explanation of why this document type was chosen',
      },
    },
    required: ['documentType', 'confidence', 'reasoning'],
  },
} as const;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Look up an extraction template for a given document type.
 *
 * @param documentType - The document type string to look up
 * @returns The matching ExtractionTemplate, or undefined if no template exists
 */
export function getExtractionTemplate(documentType: string): ExtractionTemplate | undefined {
  return EXTRACTION_TEMPLATES[documentType];
}
